import type {
  EnrichedOpportunity,
  MarketOpportunity,
  NewsSentimentSummary,
  RelatedNews,
  TechnicalIndicators,
  TradeSignal,
} from '@/models/marketData';

const safeRatio = (numerator: number, denominator: number): number =>
  denominator > 0 ? numerator / denominator : 0;

export const calculateRiskPercent = (entry: number, stopLoss: number): number =>
  safeRatio(Math.max(0, entry - stopLoss), entry) * 100;

export const calculateTargetUpsidePercent = (entry: number, target: number): number =>
  safeRatio(Math.max(0, target - entry), entry) * 100;

export const calculateRiskReward = (entry: number, stopLoss: number, target: number): number =>
  safeRatio(Math.max(0, target - entry), Math.max(0, entry - stopLoss));

export const isEmaAligned = (indicators: TechnicalIndicators): boolean =>
  indicators.close > indicators.ema20 &&
  indicators.ema20 > indicators.ema50 &&
  indicators.ema50 > indicators.ema200;

export const isMomentumReady = (indicators: TechnicalIndicators): boolean =>
  indicators.adx14 >= 25 && indicators.rsi14 >= 55 && indicators.rsi14 <= 70;

export const hasFreshMacdCrossover = (reasons: string[]): boolean =>
  reasons.some((reason) => reason.toLowerCase().includes('fresh bullish macd crossover'));

export const sentimentType = (score: number): 'positive' | 'neutral' | 'negative' => {
  if (score >= 0.25) return 'positive';
  if (score <= -0.25) return 'negative';
  return 'neutral';
};

export const aggregateNewsSentiment = (news: RelatedNews[]): NewsSentimentSummary => {
  if (news.length === 0) return { score: null, type: 'none', label: 'No news' };
  const scores = news.map(({ sentiment }) => sentiment).filter(Number.isFinite);
  if (scores.length === 0) return { score: 0, type: 'neutral', label: 'Neutral' };
  const score = scores.reduce((total, value) => total + value, 0) / scores.length;
  const types = new Set(scores.map(sentimentType));
  if (types.has('positive') && types.has('negative')) {
    return { score, type: 'mixed', label: 'Mixed' };
  }
  const type = sentimentType(score);
  return { score, type, label: `${type[0].toUpperCase()}${type.slice(1)}` };
};

export const getSignalLabel = (signal: TradeSignal): string =>
  ({ 5: 'Constructive', 4: 'Positive', 3: 'Watchlist' })[signal];

export const enrichOpportunity = (opportunity: MarketOpportunity): EnrichedOpportunity => ({
  ...opportunity,
  metrics: {
    riskPercent: calculateRiskPercent(opportunity.suggestedEntry, opportunity.stopLoss),
    target1UpsidePercent: calculateTargetUpsidePercent(
      opportunity.suggestedEntry,
      opportunity.target1,
    ),
    target2UpsidePercent: calculateTargetUpsidePercent(
      opportunity.suggestedEntry,
      opportunity.target2,
    ),
    target1RiskReward: calculateRiskReward(
      opportunity.suggestedEntry,
      opportunity.stopLoss,
      opportunity.target1,
    ),
    target2RiskReward: calculateRiskReward(
      opportunity.suggestedEntry,
      opportunity.stopLoss,
      opportunity.target2,
    ),
    emaAligned: isEmaAligned(opportunity.indicators),
    momentumReady: isMomentumReady(opportunity.indicators),
    freshMacdCrossover: hasFreshMacdCrossover(opportunity.reasons),
    macdBullish: opportunity.indicators.macd > opportunity.indicators.macdSignal,
    newsSentiment: aggregateNewsSentiment(opportunity.relatedNews),
  },
});
